import type {
  OperatorToken,
  OperatorTokenList,
  OperatorTokenMinted,
} from '@matter_extractor/shared';
import {
  operatorTokenListSchema,
  operatorTokenMintedSchema,
  operatorTokenSchema,
} from '@matter_extractor/shared';
import { ApiError } from './api';

/**
 * The three calls of Réglages > Jetons (§20.2, issue #84) - the personal
 * operator tokens of the MCP assistant.
 *
 * ## The credential is the COOKIE, and only the cookie
 *
 * `routes/admin-tokens.ts` is the one module of the backend whose guard is not
 * `requireAdminAccess`: a token does not strike a token, and neither does the
 * shared one. So `credentials: 'include'` is not a habit copied from the
 * neighbouring files here - it is the ONLY thing that opens these three
 * routes, and a call made without it reads exactly like a call made by a
 * stranger.
 *
 * The whole shape of the screen follows from that: a session opened by the
 * shared machine token is answered 401 on all three, and the tab says so in
 * French rather than letting the shell reopen a door on a credential that was
 * never going to work.
 *
 * ## Why a file of its own rather than another family in `lib/api.ts`
 *
 * The reason `links-api.ts` and `rules-api.ts` give, and it holds one lot
 * later: `api.ts` is a four-hundred line module several lots edit, and a merge
 * that goes wrong there goes wrong in the only place it cannot be seen - a
 * request that silently keeps the other branch's shape. `ApiError` is IMPORTED
 * from there rather than re-declared: it is the type every screen catches, and
 * two classes of the same name would make `instanceof` answer false on one.
 *
 * Every answer is PARSED before it is returned: a response is an external
 * input even when we wrote the server. The minted one is parsed by its own
 * schema - the view PLUS the clear token, the one shape of this repo that
 * carries a secret.
 */

const API_URL: string = import.meta.env.VITE_API_URL ?? 'http://localhost:3001';

/**
 * WHERE the API is, spelled the way a poste has to spell it.
 *
 * The same derivation `chatSocketUrl` makes in `api.ts`, and for the same
 * reason: there is one notion of where the backend is, and it is the bundle's.
 * `.env.production` ships an EMPTY `VITE_API_URL` so the calls go same-origin
 * through nginx, and the empty string is then the page's origin; `make dev`
 * and the stub stacks put the backend on another port, and that port is what
 * the bundle was built with.
 *
 * It is exported because the one-time panel PRINTS it: `MATTER_API_URL` is the
 * address `.mcp.json` will reach, so an operator who pasted
 * `window.location.origin` off a two-port dev stack would wire Claude Code to
 * the Vite server and read a 404 from a screen that told them to.
 */
export function apiBaseUrl(): string {
  return API_URL === '' ? window.location.origin : API_URL;
}

/**
 * The cookie travels, and nothing else does.
 *
 * Same object as `ADMIN_INIT` of `api.ts` and of the two sibling API modules,
 * and `as const satisfies` for the same reason: `credentials` has to keep its
 * literal type.
 */
const ADMIN_INIT = {
  headers: { 'content-type': 'application/json' },
  credentials: 'include',
} as const satisfies RequestInit;

async function request(path: string, init?: RequestInit): Promise<unknown> {
  const response = await fetch(`${API_URL}${path}`, { ...ADMIN_INIT, ...init });
  if (!response.ok) {
    let message = 'La requête a échoué.';
    let details: unknown;
    let code: string | null = null;
    try {
      const body = (await response.json()) as {
        error?: string;
        message?: string;
        details?: unknown;
      };
      if (body.message) message = body.message;
      details = body.details ?? body;
      code = body.error ?? null;
    } catch {
      // Non-JSON error body: keep the generic message.
    }
    throw new ApiError(response.status, message, details, code);
  }
  return response.json();
}

/** Mine, revoked ones included, newest first - and never a fingerprint (§20.2). */
export function fetchOperatorTokens(): Promise<OperatorTokenList> {
  return request('/api/admin/tokens').then((body) => operatorTokenListSchema.parse(body));
}

/**
 * Strikes a token on a label. The answer carries the clear value ONCE.
 *
 * Nothing in this module keeps it: it is handed to the screen, held in React
 * state for as long as the panel is open, and dropped. No route, no log and no
 * column can produce it a second time.
 */
export function mintOperatorToken(label: string): Promise<OperatorTokenMinted> {
  return request('/api/admin/tokens', { method: 'POST', body: JSON.stringify({ label }) }).then(
    (body) => operatorTokenMintedSchema.parse(body),
  );
}

/** Revokes one of mine. The row stays in the list, dated, and opens nothing. */
export function revokeOperatorToken(id: string): Promise<OperatorToken> {
  return request(`/api/admin/tokens/${id}`, { method: 'DELETE' }).then((body) =>
    operatorTokenSchema.parse(body),
  );
}
